<script setup>
import { CheckIcon } from "lucide-vue-next";
import { twMerge } from "tailwind-merge";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps({
    as: {
        type: [Object, String],
        default: "button",
    },
    type: {
        type: String,
        default: "submit",
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    processing: {
        type: Boolean,
        default: false,
    },
    success: {
        type: Boolean,
        default: false,
    },
    icon: {
        type: [Object, Function],
        required: false,
    },
    z: {
        type: Number,
        default: 0,
    },
    class: String,
});

const { t: __ } = useI18n();

const _disabled = computed(() => {
    if (props.disabled || props.processing) {
        return true;
    }

    return false;
});

const buttonClasses = computed(() => {
    let classes = [
        _disabled.value ? "pointer-events-none" : "",
        "text-gray-900 dark:text-white hover:shadow-sm relative inline-flex items-center px-3 py-2 text-sm font-medium rounded-md",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
        "gap-2.5",
    ];

    switch (props.z) {
        case 3:
            classes.push("bg-gray-200 hover:bg-gray-300 disabled:bg-gray-200 focus-visible:ring-gray-600 focus-visible:ring-offset-gray-200");
            classes.push(
                "dark:bg-gray-700 dark:hover:bg-gray-600 dark:disabled:bg-gray-700 dark:focus-visible:ring-gray-600 dark:focus-visible:ring-offset-gray-700"
            );
            break;
        case 2:
            classes.push("bg-gray-100 hover:bg-gray-200 disabled:bg-gray-100 focus-visible:ring-gray-500 focus-visible:ring-offset-gray-100");
            classes.push(
                "dark:bg-gray-800 dark:hover:bg-gray-700 dark:disabled:bg-gray-800 dark:focus-visible:ring-gray-700 dark:focus-visible:ring-offset-gray-800"
            );
            break;
        case 1:
            classes.push("bg-gray-50 hover:bg-gray-200 disabled:bg-gray-50 focus-visible:ring-gray-400 focus-visible:ring-offset-gray-50");
            classes.push(
                "dark:bg-gray-900 dark:hover:bg-gray-800 dark:disabled:bg-gray-900 dark:focus-visible:ring-gray-800 dark:focus-visible:ring-offset-gray-900"
            );
            break;
        default:
            classes.push("bg-white hover:bg-gray-100 disabled:bg-white focus-visible:ring-gray-300 focus-visible:ring-offset-white");
            classes.push(
                "dark:bg-gray-950 dark:hover:bg-gray-900 dark:disabled:bg-gray-950 dark:focus-visible:ring-gray-900 dark:focus-visible:ring-offset-gray-950"
            );
            break;
    }

    return twMerge(classes, props.class);
});
</script>

<template>
    <component :is="as" :type="type" :disabled="_disabled" :class="buttonClasses">
        <CheckIcon v-if="success" class="flex-shrink-0 w-4 h-4" />
        <svg v-else-if="processing" class="flex-shrink-0 w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <slot v-else name="icon">
            <component v-if="icon" :is="icon" class="flex-shrink-0 w-4 h-4" />
        </slot>
        <slot />
    </component>
</template>
